/**
 * Design Studio — an immersive, real-time storefront design experience.
 *
 * A full-screen creative tool: a faithful live preview of the storefront on
 * the left, a control deck on the right with two modes —
 *   • Vibes   : one-tap complete personalities (palette + every axis)
 *   • Studio  : independent fine control over each design axis
 *
 * Every change re-renders the preview instantly. The preview is rendered
 * through the *same* CSS-variable + data-attribute pipeline the real
 * storefront uses (`getStoreDesignStyle` / `getStoreDesignAttrs`), so what
 * the admin sees here is exactly what their customers will get.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
    X, Sparkles, SlidersHorizontal, Check, Monitor, Smartphone,
    Search, ShoppingBag, Store as StoreIcon, ChevronLeft, RotateCcw, Loader2,
} from 'lucide-react';
import Portal from '../../../Portal';
import { getStoreThemeSwatch } from '../../../themes/applyStoreTheme';
import { getStoreDesignStyle, getStoreDesignAttrs } from '../../../themes/applyStoreDesign';
import { STORE_THEMES, STORE_THEME_CATEGORIES } from '../../../themes/storeThemes';
import {
    VIBES, vibeToConfig, matchesVibe, resolveDesign, DEFAULT_DESIGN,
    FONT_PAIRS, RADII, DENSITIES, CARD_STYLES, COVER_STYLES,
    TEXTURES, MOTIONS, BUTTON_STYLES, findFontPair,
} from '../../../themes/storeDesign';

/* ───────────────────────── live preview ───────────────────────── */

const MOCK_PRODUCTS = [
    { name: 'לחם מחמצת כפרי', price: 28, tag: 'חדש', grad: 'from-brand-300 to-brand-600', icon: '🍞' },
    { name: 'ריבת תאנים ביתית', price: 34, grad: 'from-secondary-500 to-brand-500', icon: '🍯' },
    { name: 'גבינת עזים טרייה', price: 42, tag: 'מומלץ', grad: 'from-brand-200 to-secondary-500', icon: '🧀' },
    { name: 'שמן זית כתית', price: 56, grad: 'from-brand-400 to-brand-700', icon: '🫒' },
];

const PreviewCard = ({ p }) => (
    <div data-card-root className="bg-surface shadow-card rounded-xl overflow-hidden flex flex-col">
        <div className={`relative aspect-[4/3] bg-gradient-to-br ${p.grad} flex items-center justify-center`}>
            <span className="text-4xl drop-shadow-sm" aria-hidden>{p.icon}</span>
            {p.tag && (
                <span className="absolute top-2 right-2 bg-brand-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-md">
                    {p.tag}
                </span>
            )}
        </div>
        <div data-card-body className="p-3 flex flex-col gap-1.5 flex-1">
            <h3 className="text-sm font-bold text-ink-strong leading-tight">{p.name}</h3>
            <div className="mt-auto flex items-center justify-between gap-2 pt-1">
                <span className="text-secondary-600 font-extrabold">₪{p.price}</span>
                <button className="btn bg-brand-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-sm">
                    הוסף
                </button>
            </div>
        </div>
    </div>
);

const StorefrontPreview = ({ design, storeName }) => {
    const style = useMemo(() => getStoreDesignStyle(design), [design]);
    const attrs = useMemo(() => getStoreDesignAttrs(design), [design]);

    return (
        <div
            {...attrs}
            style={style}
            dir="rtl"
            className="bg-surface-base text-ink min-h-full"
        >
            {/* cover */}
            <div data-store-cover className="relative">
                <div
                    data-store-cover-media
                    className="relative aspect-[16/9] w-full overflow-hidden bg-gradient-to-br from-brand-300 via-brand-400 to-brand-700"
                >
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                </div>
                <div className="absolute inset-x-0 bottom-0 p-5">
                    <h1 data-store-name className="text-2xl font-bold text-white drop-shadow-lg">
                        {storeName || 'החנות שלי'}
                    </h1>
                    <p className="text-white/85 text-xs font-medium drop-shadow">מוצרים טריים, ישר מהיצרן</p>
                </div>
            </div>

            {/* tabs */}
            <div className="flex items-center gap-5 px-5 border-b border-line bg-surface">
                {['מוצרים', 'אודות', 'שאלות נפוצות'].map((t, i) => (
                    <span
                        key={t}
                        className={`py-3 text-sm font-bold border-b-2 ${
                            i === 0 ? 'border-brand-500 text-brand-700' : 'border-transparent text-ink-subtle'
                        }`}
                    >
                        {t}
                    </span>
                ))}
            </div>

            {/* toolbar */}
            <div className="px-5 pt-4 flex items-center gap-2">
                <div className="flex-1 flex items-center gap-2 bg-surface border border-line rounded-lg px-3 py-2">
                    <Search className="w-4 h-4 text-ink-faint" />
                    <span className="text-xs text-ink-faint">חיפוש מוצר…</span>
                </div>
                <button className="btn bg-brand-500 text-white text-xs font-bold px-4 py-2 rounded-lg shadow-sm">
                    סינון
                </button>
            </div>

            {/* category chips */}
            <div className="px-5 pt-3 flex gap-2 flex-wrap">
                {['הכל', 'מאפים', 'גבינות', 'שמנים'].map((c, i) => (
                    <span
                        key={c}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-xl border ${
                            i === 0
                                ? 'bg-brand-500 text-white border-brand-500'
                                : 'bg-surface text-ink-muted border-line'
                        }`}
                    >
                        {c}
                    </span>
                ))}
            </div>

            {/* product grid */}
            <div className="grid grid-cols-2 gap-4 p-5">
                {MOCK_PRODUCTS.map((p) => <PreviewCard key={p.name} p={p} />)}
            </div>

            {/* floating cart */}
            <div className="sticky bottom-4 px-5 pb-5">
                <button className="btn w-full bg-brand-600 text-white font-bold py-3 rounded-xl shadow-elevated flex items-center justify-center gap-2">
                    <ShoppingBag className="w-5 h-5" />
                    לעגלה · ₪62
                </button>
            </div>
        </div>
    );
};

/* ───────────────────────── control primitives ───────────────────────── */

const ControlRow = ({ label, sub, children }) => (
    <div className="space-y-2">
        <div>
            <p className="text-sm font-bold text-ink-strong">{label}</p>
            {sub && <p className="text-xs text-ink-subtle mt-0.5">{sub}</p>}
        </div>
        <div className="flex flex-wrap gap-2">{children}</div>
    </div>
);

const Chip = ({ active, onClick, children, title }) => (
    <button
        type="button"
        onClick={onClick}
        title={title}
        aria-pressed={active}
        className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${
            active
                ? 'bg-brand-500 text-white border-brand-500 shadow-sm'
                : 'bg-surface text-ink-muted border-line hover:border-brand-400 hover:text-ink-strong'
        }`}
    >
        {children}
    </button>
);

const RadiusGlyph = ({ scale, active }) => (
    <span
        className={`inline-block w-5 h-5 border-2 ${active ? 'border-white' : 'border-ink-subtle'}`}
        style={{ borderRadius: `${Math.min(scale * 0.5, 0.7) * 20}px` }}
    />
);

/* ───────────────────────── the studio ───────────────────────── */

export const DesignStudio = ({ open, store, value, onApply, onClose }) => {
    const [design, setDesign] = useState(() => resolveDesign(value));
    const [mode, setMode] = useState('vibes'); // vibes | studio
    const [device, setDevice] = useState('phone'); // phone | desktop
    const [showPalette, setShowPalette] = useState(false);
    const [saving, setSaving] = useState(false);
    const [mobilePane, setMobilePane] = useState('design'); // mobile only: design | preview

    useEffect(() => { if (open) { setDesign(resolveDesign(value)); setSaving(false); } }, [open, value]);

    // Persist via the parent. Resolves on success (parent closes the studio);
    // on failure we stay open so the choice isn't lost.
    const handleApply = async () => {
        if (saving) return;
        setSaving(true);
        try {
            await onApply?.(resolveDesign(design));
        } catch {
            /* parent surfaces the error message */
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    const set = (patch) => setDesign((d) => resolveDesign({ ...d, ...patch, vibe: null }));
    const activeVibe = VIBES.find((v) => matchesVibe(design, v));
    const dirty = JSON.stringify(resolveDesign(design)) !== JSON.stringify(resolveDesign(value));

    const themesByCat = STORE_THEME_CATEGORIES
        .map((cat) => ({ cat, items: STORE_THEMES.filter((t) => t.category === cat.id) }))
        .filter((g) => g.items.length);

    // Frame fills the available pane height (responsive) instead of a fixed
    // pixel size, so it never overflows a phone screen.
    const frameCls = device === 'phone'
        ? 'w-full max-w-[360px] h-full max-h-full rounded-[1.75rem] border-[7px] border-ink-strong'
        : 'w-full max-w-[920px] h-full max-h-full rounded-xl border border-line-strong';

    return (
        <Portal containerId="design-studio-root">
            <div dir="rtl" className="fixed inset-0 z-[2147483600] flex flex-col bg-surface-sunken">
                {/* ── top bar ── */}
                <header className="shrink-0 flex items-center justify-between gap-2 sm:gap-4 px-3 sm:px-6 py-2.5 sm:py-3 bg-surface border-b border-line">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                        <span className="hidden sm:inline-flex shrink-0 p-2 rounded-xl bg-brand-50 text-brand-600 ring-1 ring-brand-100">
                            <Sparkles className="w-5 h-5" />
                        </span>
                        <div className="min-w-0">
                            <h2 className="text-sm sm:text-base font-extrabold text-ink-strong leading-tight">סטודיו העיצוב</h2>
                            <p className="hidden sm:block text-xs text-ink-subtle truncate">
                                {activeVibe ? `וייב: ${activeVibe.name}` : 'עיצוב מותאם אישית'} · {store?.name || 'החנות שלי'}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                        <div className="hidden sm:flex items-center bg-surface-muted rounded-lg p-0.5">
                            {[['phone', Smartphone], ['desktop', Monitor]].map(([d, Icon]) => (
                                <button
                                    key={d}
                                    onClick={() => setDevice(d)}
                                    title={d === 'phone' ? 'תצוגת נייד' : 'תצוגת מחשב'}
                                    className={`p-1.5 rounded-md transition-colors ${
                                        device === d ? 'bg-surface text-brand-600 shadow-sm' : 'text-ink-subtle'
                                    }`}
                                >
                                    <Icon className="w-4 h-4" />
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={() => setDesign(resolveDesign(value))}
                            disabled={!dirty || saving}
                            title="אפס לשמור האחרון"
                            className="p-2 rounded-lg text-ink-subtle hover:text-ink-strong hover:bg-surface-muted disabled:opacity-40 transition-colors"
                        >
                            <RotateCcw className="w-4 h-4" />
                        </button>
                        <button
                            onClick={onClose}
                            disabled={saving}
                            className="px-2.5 sm:px-3 py-2 rounded-lg text-sm font-bold text-ink-muted hover:bg-surface-muted disabled:opacity-40 transition-colors"
                        >
                            בטל
                        </button>
                        <button
                            onClick={handleApply}
                            disabled={!dirty || saving}
                            className="px-3 sm:px-4 py-2 rounded-lg text-sm font-extrabold bg-brand-500 text-white shadow-sm hover:bg-brand-600 disabled:opacity-50 transition-colors inline-flex items-center gap-1.5 sm:gap-2 shrink-0"
                        >
                            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                            {saving ? 'שומר…' : 'החל ופרסם'}
                        </button>
                    </div>
                </header>

                {/* ── mobile pane switch (hidden on desktop where panes sit side-by-side) ── */}
                <div className="lg:hidden shrink-0 flex p-1.5 gap-1 bg-surface border-b border-line">
                    {[
                        ['design', 'עיצוב', SlidersHorizontal],
                        ['preview', 'תצוגה מקדימה', Smartphone],
                    ].map(([p, label, Icon]) => (
                        <button
                            key={p}
                            onClick={() => setMobilePane(p)}
                            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-bold transition-colors ${
                                mobilePane === p ? 'bg-brand-50 text-brand-700' : 'text-ink-subtle'
                            }`}
                        >
                            <Icon className="w-4 h-4" /> {label}
                        </button>
                    ))}
                </div>

                {/* ── body ── */}
                <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
                    {/* preview canvas */}
                    <div className={`${mobilePane === 'preview' ? 'flex' : 'hidden'} lg:flex flex-1 min-h-0 overflow-hidden items-center justify-center p-3 sm:p-8 bg-[radial-gradient(circle_at_center,var(--color-surface-muted),var(--color-surface-sunken))]`}>
                        <div className={`${frameCls} bg-surface overflow-hidden shadow-2xl transition-all duration-300`}>
                            <div className="w-full h-full overflow-y-auto overflow-x-hidden">
                                <StorefrontPreview design={design} storeName={store?.name} />
                            </div>
                        </div>
                    </div>

                    {/* control deck */}
                    <aside className={`${mobilePane === 'design' ? 'flex' : 'hidden'} lg:flex flex-1 lg:flex-none min-h-0 w-full lg:w-[400px] bg-surface lg:border-r border-line flex-col`}>
                        <div className="shrink-0 flex p-2 gap-1 border-b border-line">
                            {[
                                ['vibes', 'וייבים', Sparkles],
                                ['studio', 'סטודיו', SlidersHorizontal],
                            ].map(([m, label, Icon]) => (
                                <button
                                    key={m}
                                    onClick={() => setMode(m)}
                                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition-colors ${
                                        mode === m ? 'bg-brand-50 text-brand-700' : 'text-ink-subtle hover:bg-surface-muted'
                                    }`}
                                >
                                    <Icon className="w-4 h-4" /> {label}
                                </button>
                            ))}
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 space-y-6">
                            {mode === 'vibes' && (
                                <>
                                    <p className="text-xs text-ink-subtle leading-relaxed">
                                        כל וייב משנה את כל מראה החנות בלחיצה אחת — פלטה, גופן, פינות, מרקם ותנועה.
                                        אפשר תמיד לכוונן הכול ידנית בלשונית <strong>סטודיו</strong>.
                                    </p>
                                    <div className="grid grid-cols-1 gap-3">
                                        {VIBES.map((v) => {
                                            const sw = getStoreThemeSwatch(v.themeId);
                                            const isActive = activeVibe?.id === v.id;
                                            return (
                                                <button
                                                    key={v.id}
                                                    onClick={() => setDesign(vibeToConfig(v))}
                                                    className={`group text-right rounded-xl border-2 overflow-hidden transition-all ${
                                                        isActive
                                                            ? 'border-brand-500 ring-2 ring-brand-200 shadow-md'
                                                            : 'border-line hover:border-brand-400 hover:shadow-sm'
                                                    }`}
                                                >
                                                    <div className="flex h-12">
                                                        <div className="flex-1" style={{ background: sw.surfaceBase }} />
                                                        <div className="flex-1" style={{ background: sw.brand }} />
                                                        <div className="flex-1" style={{ background: sw.secondary }} />
                                                        <div className="flex-1" style={{ background: sw.brandDark }} />
                                                        <div
                                                            className="flex-[2] grid place-items-center"
                                                            style={{ background: sw.surface, color: sw.ink, fontFamily: findFontPair(v.fontPair).display }}
                                                        >
                                                            <span className="text-lg font-bold">Aa</span>
                                                        </div>
                                                    </div>
                                                    <div className="p-3 bg-surface flex items-start justify-between gap-2">
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-extrabold text-ink-strong">{v.name}</p>
                                                            <p className="text-xs text-ink-subtle leading-snug mt-0.5">{v.tagline}</p>
                                                        </div>
                                                        {isActive
                                                            ? <Check className="w-5 h-5 text-brand-600 shrink-0" />
                                                            : <ChevronLeft className="w-4 h-4 text-ink-faint shrink-0 group-hover:text-brand-500" />}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </>
                            )}

                            {mode === 'studio' && (
                                <>
                                    {/* palette */}
                                    <ControlRow label="פלטת צבעים" sub={`50 פלטות · ${STORE_THEMES.find(t => t.id === design.themeId)?.name || ''}`}>
                                        <button
                                            type="button"
                                            onClick={() => setShowPalette((s) => !s)}
                                            className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-line bg-surface hover:border-brand-400 transition-colors"
                                        >
                                            <span className="flex items-center gap-2">
                                                {(() => {
                                                    const s = getStoreThemeSwatch(design.themeId);
                                                    return ['surfaceBase', 'brand', 'secondary', 'ink'].map((k) => (
                                                        <span key={k} className="w-5 h-5 rounded-md border border-line" style={{ background: s[k] }} />
                                                    ));
                                                })()}
                                            </span>
                                            <span className="text-xs font-bold text-ink-muted">
                                                {showPalette ? 'סגור' : 'בחר פלטה'}
                                            </span>
                                        </button>
                                        {showPalette && (
                                            <div className="w-full mt-2 max-h-72 overflow-y-auto pr-1 space-y-3 border border-line rounded-lg p-3 bg-surface-muted">
                                                {themesByCat.map(({ cat, items }) => (
                                                    <div key={cat.id} className="space-y-1.5">
                                                        <p className="text-[10px] font-extrabold text-ink-subtle uppercase tracking-wide">{cat.label}</p>
                                                        <div className="grid grid-cols-3 gap-1.5">
                                                            {items.map((t) => {
                                                                const s = getStoreThemeSwatch(t.id);
                                                                const sel = design.themeId === t.id;
                                                                return (
                                                                    <button
                                                                        key={t.id}
                                                                        onClick={() => set({ themeId: t.id })}
                                                                        title={t.name}
                                                                        className={`rounded-md overflow-hidden border-2 ${sel ? 'border-brand-500 ring-1 ring-brand-300' : 'border-transparent hover:border-brand-300'}`}
                                                                    >
                                                                        <span className="flex h-7">
                                                                            <span className="flex-1" style={{ background: s.surfaceBase }} />
                                                                            <span className="flex-1" style={{ background: s.brand }} />
                                                                            <span className="flex-1" style={{ background: s.secondary }} />
                                                                            <span className="flex-1" style={{ background: s.brandDark }} />
                                                                        </span>
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </ControlRow>

                                    {/* typography */}
                                    <ControlRow label="טיפוגרפיה" sub="זוג גופנים — כותרת וטקסט">
                                        {FONT_PAIRS.map((f) => (
                                            <button
                                                key={f.id}
                                                onClick={() => set({ fontPair: f.id })}
                                                className={`px-3 py-2 rounded-lg border text-right transition-all ${
                                                    design.fontPair === f.id
                                                        ? 'bg-brand-500 text-white border-brand-500 shadow-sm'
                                                        : 'bg-surface text-ink-muted border-line hover:border-brand-400'
                                                }`}
                                            >
                                                <span className="block text-lg leading-none" style={{ fontFamily: f.display }}>אבג Aa</span>
                                                <span className="block text-[10px] mt-1 opacity-80">{f.label}</span>
                                            </button>
                                        ))}
                                    </ControlRow>

                                    {/* corners */}
                                    <ControlRow label="שפת פינות" sub="האופי הגיאומטרי של הכרטיסים והכפתורים">
                                        {RADII.map((r) => (
                                            <button
                                                key={r.id}
                                                onClick={() => set({ radius: r.id })}
                                                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold border transition-all ${
                                                    design.radius === r.id
                                                        ? 'bg-brand-500 text-white border-brand-500'
                                                        : 'bg-surface text-ink-muted border-line hover:border-brand-400'
                                                }`}
                                            >
                                                <RadiusGlyph scale={r.scale} active={design.radius === r.id} />
                                                {r.label}
                                            </button>
                                        ))}
                                    </ControlRow>

                                    {/* density */}
                                    <ControlRow label="צפיפות" sub="כמה אוויר יש בין האלמנטים">
                                        {DENSITIES.map((d) => (
                                            <Chip key={d.id} active={design.density === d.id} onClick={() => set({ density: d.id })}>
                                                {d.label}
                                            </Chip>
                                        ))}
                                    </ControlRow>

                                    {/* card style */}
                                    <ControlRow label="כרטיס מוצר" sub="האנטומיה של כל מוצר ברשת">
                                        {CARD_STYLES.map((c) => (
                                            <Chip key={c.id} active={design.card === c.id} onClick={() => set({ card: c.id })} title={c.hint}>
                                                {c.label}
                                            </Chip>
                                        ))}
                                    </ControlRow>

                                    {/* cover */}
                                    <ControlRow label="כותרת החנות" sub="הרושם הראשון בראש העמוד">
                                        {COVER_STYLES.map((c) => (
                                            <Chip key={c.id} active={design.cover === c.id} onClick={() => set({ cover: c.id })} title={c.hint}>
                                                {c.label}
                                            </Chip>
                                        ))}
                                    </ControlRow>

                                    {/* texture */}
                                    <ControlRow label="מרקם רקע" sub="שכבת אווירה עדינה על כל הדף">
                                        {TEXTURES.map((t) => (
                                            <Chip key={t.id} active={design.texture === t.id} onClick={() => set({ texture: t.id })}>
                                                {t.label}
                                            </Chip>
                                        ))}
                                    </ControlRow>

                                    {/* motion */}
                                    <ControlRow label="תנועה" sub="אנרגיית האנימציות במגע">
                                        {MOTIONS.map((m) => (
                                            <Chip key={m.id} active={design.motion === m.id} onClick={() => set({ motion: m.id })}>
                                                {m.label}
                                            </Chip>
                                        ))}
                                    </ControlRow>

                                    {/* buttons */}
                                    <ControlRow label="כפתורים" sub="צורת כפתורי הפעולה">
                                        {BUTTON_STYLES.map((b) => (
                                            <Chip key={b.id} active={design.button === b.id} onClick={() => set({ button: b.id })}>
                                                {b.label}
                                            </Chip>
                                        ))}
                                    </ControlRow>

                                    <button
                                        onClick={() => setDesign(resolveDesign({ ...DEFAULT_DESIGN, themeId: design.themeId }))}
                                        className="w-full py-2 rounded-lg border border-dashed border-line text-xs font-bold text-ink-subtle hover:text-ink-strong hover:border-brand-400 transition-colors"
                                    >
                                        אפס מבנה לברירת מחדל (שמור על הפלטה)
                                    </button>
                                </>
                            )}
                        </div>
                    </aside>
                </div>
            </div>
        </Portal>
    );
};

export default DesignStudio;
